import { ArrowLeft, Clock, HelpCircle, MailCheck, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AuthLayout, signUpSteps } from './AuthLayout'
import { Button } from '@/components/ui/Button'
import { Notice } from '@/components/ui/misc'
import { authClient } from '@/lib/auth-client'

export function VerifyEmail() {
  const [params] = useSearchParams()
  const email = params.get('email') ?? ''
  const [sending, setSending] = useState(false)

  const resend = async () => {
    if (!email) return
    setSending(true)
    const { error } = await authClient.sendVerificationEmail({
      email,
      callbackURL: '/registration-submitted',
    })
    setSending(false)
    toast[error ? 'error' : 'success'](
      error ? 'Could not resend the verification email.' : 'Verification email sent.',
    )
  }

  return (
    <AuthLayout steps={signUpSteps(3)}>
      <div className="flex flex-col items-center text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-brand-soft text-brand">
          <MailCheck className="size-6" />
        </span>
        <h1 className="mt-5 font-display text-[26px] font-semibold text-ink">
          Verify your email address
        </h1>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
          We sent a verification link to <strong className="text-ink">{email || 'your address'}</strong>.
          Follow the link to confirm your address and activate your DyeAtlas account.
        </p>
      </div>

      <div className="my-5">
        <Notice
          tone="neutral"
          icon={<HelpCircle className="size-4" />}
          title="The email may take a few minutes to arrive."
        >
          Check your spam or quarantine folder. Verification links expire after 24
          hours.
        </Notice>
      </div>

      <Button fullWidth onClick={resend} loading={sending} disabled={!email}>
        Resend verification email
        <RefreshCw className="size-4" />
      </Button>

      <p className="mt-3 text-center text-[13px] text-ink-muted">
        Not your address?{' '}
        <Link to="/sign-up" className="font-semibold text-brand hover:underline">
          Change email
        </Link>
      </p>

      <div className="my-5 h-px bg-line" />

      <Link
        to="/sign-in"
        className="flex items-center justify-center gap-1.5 text-[13px] font-semibold text-ink-soft hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Back to sign in
      </Link>
    </AuthLayout>
  )
}

/** Shown after the verification link is followed, while an administrator reviews. */
export function RegistrationSubmitted() {
  const [params] = useSearchParams()
  const email = params.get('email')

  return (
    <AuthLayout steps={[
      { label: 'Account', state: 'done' },
      { label: 'Profile', state: 'done' },
      { label: 'Verify', state: 'done' },
    ]}>
      <div className="flex flex-col items-center text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-brand-soft text-brand">
          <Clock className="size-6" />
        </span>
        <h1 className="mt-5 font-display text-[26px] font-semibold text-ink">
          Registration submitted
        </h1>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
          Your registration information has been sent to the admin for review. You&apos;ll be
          informed by email when the admin approves your registration.
        </p>
        <span className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-caution/25 bg-caution-soft px-3 py-1 text-[11px] font-medium text-caution">
          <span className="size-1.5 rounded-full bg-caution" />
          Awaiting admin approval
        </span>
      </div>

      <div className="my-5">
        <Notice tone="caution" icon={<HelpCircle className="size-4" />} title="What happens next?">
          We&apos;ll send your approval notification to{' '}
          <strong>{email ?? 'your email address'}</strong>.
          <br />
          <br />
          Your account is not active yet. No further action is needed while your registration is
          being reviewed.
        </Notice>
      </div>

      <Link to="/sign-in">
        <Button fullWidth>
          <ArrowLeft className="size-4" />
          Back to sign in
        </Button>
      </Link>
    </AuthLayout>
  )
}
